import {
  Controller, ForbiddenException, Get, Logger, NotFoundException, Param, Req, Res,
  ServiceUnavailableException, UseGuards,
} from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiParam, ApiResponse, ApiSecurity, ApiTags } from '@nestjs/swagger';
import type { Response } from 'express';
import { PrismaService } from '@cart/api-data-access';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { InvoicePdfService } from './invoice-pdf.service';
import { InvoiceLinkService } from './invoice-link.service';
import { InvoiceService } from './invoice.service';

/**
 * Invoice downloads.
 *
 * Streamed through an authenticated endpoint rather than handed out as a
 * pre-signed URL. An invoice carries the customer's name, address and phone;
 * a signed URL is forwardable, lands in browser history, referrer headers and
 * proxy logs, and keeps working for whoever ends up holding it until it
 * expires. Streaming costs one hop through the API and answers "who is allowed
 * to see this" on every single request.
 */
@ApiTags('Invoices')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller()
export class InvoicesController {
  private readonly logger = new Logger(InvoicesController.name);

  constructor(
    private readonly invoices: InvoiceService,
    private readonly pdfs: InvoicePdfService,
    private readonly links: InvoiceLinkService,
    private readonly prisma: PrismaService
  ) {}

  /**
   * The emailed link. Deliberately unauthenticated — see InvoiceLinkService for
   * why this one route is, and why the others are not.
   *
   * The token proves nothing about who is asking, only which invoice they were
   * sent, so it can reach exactly one document and expires.
   */
  @Get('invoices/d/:token')
  @ApiOperation({ summary: 'Download an invoice from a signed emailed link' })
  @ApiParam({ name: 'token', type: String })
  @ApiResponse({ status: 200, description: 'PDF stream' })
  async fromLink(@Param('token') token: string, @Res() response: Response): Promise<void> {
    const invoiceId = this.links.verify(token);

    // Expired and forged are answered identically: distinguishing them tells an
    // attacker whether an id was ever real.
    if (!invoiceId) {
      throw new NotFoundException('That invoice link is not valid, or has expired');
    }

    const pdf = await this.pdfs.pdfFor(invoiceId);

    response.setHeader('Content-Type', 'application/pdf');
    response.setHeader('Content-Length', pdf.buffer.byteLength);
    response.setHeader('Content-Disposition', `inline; filename="${pdf.fileName}"`);
    // Never a shared cache: the link is public but the document is not.
    response.setHeader('Cache-Control', 'private, no-store');
    response.end(pdf.buffer);
  }

  /** The merchant's copy, from the admin order list. */
  @Get('admin/orders/:id/invoice')
  @UseGuards(AuthGuard, AdminAccessGuard)
  @ApiOperation({ summary: 'Download the tax invoice for an order (merchant)' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'PDF stream' })
  async merchantCopy(
    @Param('id') orderId: string,
    @Req() request: GuardedRequest,
    @Res() response: Response
  ): Promise<void> {
    const storeId = request.headers['x-store-id'];
    const order = await this.prisma.order.findUnique({
      where: { id: orderId },
      select: { id: true, storeId: true },
    });

    if (!order) throw new NotFoundException('Order not found');

    // AdminAccessGuard proves this user administers the store they claimed; it
    // does not prove the order belongs to that store.
    if (typeof storeId === 'string' && storeId && order.storeId !== storeId) {
      throw new ForbiddenException('That order belongs to a different store');
    }

    await this.send(orderId, request.user.id, response);
  }

  /** The customer's own copy. */
  @Get('orders/:id/invoice')
  @UseGuards(AuthGuard)
  @ApiOperation({ summary: 'Download the tax invoice for your order' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'PDF stream' })
  async customerCopy(
    @Param('id') orderId: string,
    @Req() request: GuardedRequest,
    @Res() response: Response
  ): Promise<void> {
    const order = await this.prisma.order.findUnique({
      where: { id: orderId },
      select: { id: true, customerId: true, email: true },
    });

    if (!order) throw new NotFoundException('Order not found');

    // `customerId` holds the user id — that is the rule the order list itself
    // uses (`orders.repository.ts`: `{ customerId: userId }`), and inventing a
    // second definition of "my order" here is how the two drift apart.
    // Guest checkouts have no customer id, so the email the order was placed
    // with is the fallback.
    const mine =
      order.customerId === request.user.id ||
      (!!order.email && order.email.toLowerCase() === request.user.email.toLowerCase());

    if (!mine) throw new ForbiddenException('That order is not yours');

    await this.send(orderId, request.user.id, response);
  }

  private async send(orderId: string, actor: string, response: Response): Promise<void> {
    const invoice = await this.invoices.ensureInvoice(orderId, actor);

    // Issuing and printing fail for completely different reasons, and a single
    // opaque 500 for both is what makes this undiagnosable from a browser tab.
    // The document exists at this point whatever happens next, so the failure
    // says so — and names the invoice, which is enough to find it in the logs.
    let pdf;
    try {
      pdf = await this.pdfs.pdfFor(invoice.id);
    } catch (error) {
      this.logger.error(
        `invoice ${invoice.invoiceNumber} is issued but could not be printed: ${(error as Error).message}`,
        (error as Error).stack
      );

      throw new ServiceUnavailableException(
        `Invoice ${invoice.invoiceNumber} exists but the PDF could not be generated. ` +
          'This is usually a missing browser on the server — the invoice is safe and ' +
          'will download once that is fixed.'
      );
    }

    response.setHeader('Content-Type', 'application/pdf');
    response.setHeader('Content-Length', pdf.buffer.byteLength);
    response.setHeader('Content-Disposition', `attachment; filename="${pdf.fileName}"`);
    // A tax invoice never changes, but it is personal — cache on the device
    // that asked for it, never in a shared proxy.
    response.setHeader('Cache-Control', 'private, max-age=3600');
    response.end(pdf.buffer);
  }
}
